import React, { ReactNode } from 'react';
import { Input } from 'antd';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import IbTypography from '../IbTypography';
import IbDivider from '../IbDivider';
import IbTextHighlighter from '../IbTextHighlighter';

const MAIN_CLASS_NAME = 'ib-card';
const HEADER_CLASS_NAME = `${MAIN_CLASS_NAME}__header`;
const TITLE_CLASS_NAME = `${HEADER_CLASS_NAME}__title`;
const EXTRA_CLASS_NAME = `${HEADER_CLASS_NAME}__extra`;
const BODY_CLASS_NAME = `${MAIN_CLASS_NAME}__body`;
const CONTENT_CLASS_NAME = `${BODY_CLASS_NAME}__content`;
const FOOTER_CLASS_NAME = `${BODY_CLASS_NAME}__footer`;

export interface IIbCardProps {
  className?: string;
  title?: string;
  displayName?: string;
  avatar?: ReactNode;
  editingTitle?: string;
  extra?: ReactNode;
  footer?: ReactNode;
  searchText?: string;
  titleIsEditing?: boolean;
  loading?: boolean;
  onClick?: () => void;
  onTitleInputBlur?: () => void;
  onTitleInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTitleInputFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onTitleInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const IbCard: React.FC<IIbCardProps> = ({
  className,
  title,
  displayName,
  avatar,
  editingTitle,
  extra,
  footer,
  children,
  searchText,
  titleIsEditing,
  loading,
  onClick,
  onTitleInputBlur,
  onTitleInputChange,
  onTitleInputFocus,
  onTitleInputKeyDown,
}) => {
  const classes = [MAIN_CLASS_NAME];
  className && classes.push(className);

  title = title ?? '';

  const renderAvatar = () => {
    if (loading) {
      return <Skeleton height={48} width={48} />;
    }

    return avatar;
  };

  const renderTitle = () => {
    if (loading) {
      return (
        <>
          <Skeleton height={20} width={160} />
          {displayName && <Skeleton height={14} width={160} />}
        </>
      );
    }

    return (
      <>
        <IbTypography>
          <h4>
            {titleIsEditing ? (
              <Input
                autoFocus
                value={editingTitle}
                onBlur={onTitleInputBlur}
                onChange={onTitleInputChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={onTitleInputFocus}
                onKeyDown={onTitleInputKeyDown}
              />
            ) : (
              <IbTextHighlighter highlightedText={searchText} text={title} />
            )}
          </h4>
        </IbTypography>
        {displayName && (
          <IbTypography.Paragraph disabled type="secondary">
            {displayName}
          </IbTypography.Paragraph>
        )}
      </>
    );
  };

  return (
    <div className={classes.join(' ')} onClick={onClick}>
      <div className={HEADER_CLASS_NAME}>
        {avatar && renderAvatar()}
        <div className={TITLE_CLASS_NAME}>{renderTitle()}</div>
        {!loading && extra && <div className={EXTRA_CLASS_NAME}>{extra}</div>}
      </div>
      <IbDivider orientation="center" type="horizontal" />
      <div className={BODY_CLASS_NAME}>
        <div className={CONTENT_CLASS_NAME}>{loading ? <Skeleton count={3} /> : children}</div>
        <div className={FOOTER_CLASS_NAME}>{!loading && footer}</div>
      </div>
    </div>
  );
};

export default IbCard;
